import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../../components/AuthContext";
import { 
  FaEye, 
  FaTimes, 
  FaImage, 
  FaUpload, 
  FaTrash, 
  FaCheckCircle,
  FaBox,
  FaTags,
  FaCube,
  FaMapMarkerAlt,
  FaPlus
} from 'react-icons/fa';
import Select from 'react-select';

export default function StockMaster() {
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Dark mode sync with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Helper function for Select styles with dark mode support
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      minHeight: '48px',
      border: state.isFocused 
        ? `2px solid ${isDark ? '#3B82F6' : '#3B82F6'}` 
        : `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
      borderRadius: '0.75rem',
      backgroundColor: isDark ? '#374151' : 'white',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#9CA3AF'
      },
      transition: 'all 0.2s'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderRadius: '0.75rem',
      border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      zIndex: 50
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3B82F6' 
        : state.isFocused 
        ? (isDark ? '#4B5563' : '#EFF6FF')
        : (isDark ? '#374151' : 'white'),
      color: state.isSelected ? 'white' : (isDark ? '#F3F4F6' : '#374151'),
      '&:hover': {
        backgroundColor: state.isSelected ? '#3B82F6' : (isDark ? '#4B5563' : '#EFF6FF')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  // Units of measure options
  const unitsOfMeasure = [
    { value: 'pcs', label: 'Pieces (pcs)' },
    { value: 'box', label: 'Box' },
    { value: 'bag', label: 'Bag' },
    { value: 'pkt', label: 'Packet (pkt)' },
    { value: 'pair', label: 'Pair' },
    { value: 'roll', label: 'Roll' },
    { value: 'set', label: 'Set' },
    { value: 'bkt', label: 'Bucket (bkt)' },
    { value: 'mtr', label: 'Meter (mtr)' },
    { value: 'sh', label: 'Sheet (sh)' },
    { value: 'can', label: 'Can' },
    { value: 'kg', label: 'Kilogram (kg)' },
    { value: 'ltr', label: 'Liter (ltr)' },
    { value: 'bundles', label: 'Bundles' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'drums', label: 'Drums' },
    { value: 'sq.ft', label: 'Square Feet (sq.ft)' },
    { value: 'sq.mtr', label: 'Square Meter (sq.mtr)' },
    { value: 'fts', label: 'Feet (fts)' },
    { value: 'nos', label: 'Numbers (nos)' },
    { value: 'rft', label: 'Running Feet (rft)' }
  ];

  const [stockImage, setStockImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedUnitOfMeasure, setSelectedUnitOfMeasure] = useState(null);

  const [locations, setLocations] = useState([]);

  const [form, setForm] = useState({
    name: '',
    brand: '',
    category: '',
    unitOfMeasure: '',
    image: null,
    stock: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBrands();
    fetchCategories();
    fetchLocations();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/brands/all`
      );
      
      if (response.data && response.data.success) {
        setBrands(response.data.brands);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch brands. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/categories`
      );
        setCategories(response.data || []);

    } catch (error) {
      console.error('Error fetching categories:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch categories. Please try again.'
      });
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${user.user.company._id}`
      );
      setLocations(response.data);
      
      // Initialize stock array with all locations
      const initialStock = response.data.map(location => ({
        location: location._id,
        freshQuantity: 0,
        damagedQuantity: 0
      }));
      
      setForm(prev => ({
        ...prev,
        stock: initialStock
      }));
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!form.name.trim()) {
      errors.name = 'Stock item name is required';
    }

    if (!selectedBrand?.value) {
      errors.brand = 'Brand is required';
    }

    if (!selectedCategory?.value) {
      errors.category = 'Category is required';
    }

    if (!selectedUnitOfMeasure?.value) {
      errors.unitOfMeasure = 'Unit of measure is required';
    }

    const hasInvalidStock = form.stock.some(item => 
      !item.location ||
      item.freshQuantity < 0 || 
      item.damagedQuantity < 0
    );

    if (hasInvalidStock) {
      errors.stock = 'Stock quantities cannot be negative';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).join('\n');
      Swal.fire('Validation Error', errorMessages, 'error');
      return;
    }

    setIsSubmitting(true);

    // Create stock array from locations
    const stockData = form.stock.map(item => ({
      location: item.location,
      freshQuantity: parseInt(item.freshQuantity) || 0,
      damagedQuantity: parseInt(item.damagedQuantity) || 0
    }));

    Swal.fire({
      title: 'Creating Stock Item',
      html: 'Please wait...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('brand', selectedBrand.value);
    formData.append('category', selectedCategory.value);
    formData.append('unitOfMeasure', selectedUnitOfMeasure.value);
    formData.append('stock', JSON.stringify(stockData));
    formData.append('createdBy', user.user._id);

    if (form.image) {
      formData.append('image', form.image);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/stock-items`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Stock item created successfully',
          timer: 1500
        });
        
        // Reset form with initial stock array
        const initialStock = locations.map(location => ({
          location: location._id,
          freshQuantity: 0,
          damagedQuantity: 0
        }));

        setForm({
          name: '',
          brand: '',
          category: '',
          unitOfMeasure: '',
          image: null,
          stock: initialStock
        });
        setSelectedBrand(null);
        setSelectedCategory(null);
        setSelectedUnitOfMeasure(null);
        setImagePreview(null);
        setStockImage(null);
      }
    } catch (error) {
      console.error('Error creating stock item:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'Failed to create stock item'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire('Error', 'Please select a valid image file (JPG, JPEG, PNG)', 'error');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire('Error', 'Image size should be less than 5MB', 'error');
        return;
      }

      setForm({ ...form, image: file });
      setStockImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, image: null });
    setStockImage(null);
    setImagePreview(null);
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      handleImageChange({ target: { files: [file] } });
    }
  };

  const ImagePreviewModal = () => (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
      showImageModal ? 'visible bg-black bg-opacity-60 backdrop-blur-sm' : 'invisible'
    }`}>
      <div className={`rounded-2xl p-6 max-w-4xl max-h-[90vh] w-full mx-4 shadow-2xl transform transition-all duration-300 ${
        showImageModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-2xl font-bold flex items-center gap-2 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <FaImage className="text-blue-500" />
            Image Preview
          </h3>
          <button
            onClick={() => setShowImageModal(false)}
            className={`p-2 rounded-full transition-colors ${
              darkMode 
                ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FaTimes size={24} />
          </button>
        </div>
        <div className="flex justify-center rounded-xl overflow-hidden">
          <img
            src={imagePreview}
            alt="Stock Item Preview"
            className="max-h-[70vh] object-contain"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen py-8 transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Modern Header */}
        <div className={`shadow-2xl rounded-2xl mb-8 border-l-4 border-green-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 mb-2">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    darkMode ? 'bg-green-500/20' : 'bg-green-50'
                  }`}>
                    <FaCube className={`text-2xl sm:text-3xl ${
                      darkMode ? 'text-green-400' : 'text-green-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      Create New Stock Item
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        darkMode ? 'bg-green-400' : 'bg-green-500'
                      } animate-pulse`}></div>
                      <p className={`text-xs sm:text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Ready to add inventory
                      </p>
                    </div>
                  </div>
                </div>
                <p className={`mt-2 text-xs sm:text-sm hidden sm:block ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Add a new stock item to your inventory with all the necessary details
                </p>
              </div>
              <div className="flex items-center">
                <div className={`px-3 sm:px-4 py-2 rounded-xl flex items-center gap-2 ${
                  darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700'
                }`}>
                  <FaTags className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium text-xs sm:text-sm">Stock Master</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className={`shadow-2xl rounded-2xl overflow-hidden ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div className={`px-4 sm:px-6 py-4 sm:py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center gap-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-blue-500/20' : 'bg-blue-50'
                }`}>
                  <FaBox className={`text-base sm:text-lg ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Stock Item Name */}
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Stock Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className={`block w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm border-2 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter stock item name"
                    required
                  />
                </div>

                {/* Brand */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Brand <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedBrand}
                    onChange={setSelectedBrand}
                    options={brands.map(brand => ({
                      value: brand._id,
                      label: brand.name
                    }))}
                    placeholder="Select brand"
                    className="basic-single"
                    classNamePrefix="select"
                    styles={getSelectStyles(darkMode)}
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Category <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    options={categories.map(category => ({
                      value: category._id,
                      label: category.name
                    }))}
                    placeholder="Select category"
                    className="basic-single"
                    classNamePrefix="select"
                    styles={getSelectStyles(darkMode)}
                    required
                  />
                </div>

                {/* Unit of Measure */}
                <div className="md:col-span-2">
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Unit of Measure <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedUnitOfMeasure}
                    onChange={setSelectedUnitOfMeasure}
                    options={unitsOfMeasure}
                    placeholder="Select unit of measure"
                    className="basic-single"
                    classNamePrefix="select"
                    styles={getSelectStyles(darkMode)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Image Section */}
            <div className={`px-4 sm:px-6 py-4 sm:py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center gap-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                  darkMode ? 'bg-purple-500/20' : 'bg-purple-50'
                }`}>
                  <FaImage className={`text-base sm:text-lg ${
                    darkMode ? 'text-purple-400' : 'text-purple-600'
                  }`} />
                </div>
                Stock Item Image (Optional)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Upload Image
                  </label>
                  <div
                    className={`flex items-center px-4 sm:px-6 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      darkMode 
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500' 
                        : 'bg-gray-50 border-gray-300 hover:bg-gray-100 hover:border-gray-400'
                    } focus-within:ring-2 focus-within:ring-blue-500`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <label className="w-full cursor-pointer flex items-center">
                      <FaUpload className={`text-xl sm:text-2xl mr-3 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-xs sm:text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {stockImage ? stockImage.name : 'Choose file or drag and drop here (optional)'}
                      </span>
                      <input
                        type="file"
                        onChange={handleImageChange}
                        accept="image/png,image/jpeg,image/jpg"
                        className="hidden"
                      />
                    </label>
                  </div>
                    
                  {imagePreview && (
                    <div className="flex items-center space-x-2 mt-4">
                      <button
                        type="button"
                        onClick={() => setShowImageModal(true)}
                        className={`flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-xl transition-all ${
                          darkMode 
                            ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                        }`}
                      >
                        <FaEye className="mr-2" />
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className={`flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-xl transition-all ${
                          darkMode 
                            ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        <FaTrash className="mr-2" />
                        Remove
                      </button>
                    </div>
                  )}
                  <p className={`mt-2 text-xs sm:text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    PNG, JPG, JPEG up to 5MB (Optional)
                  </p>
                </div>

                {imagePreview && (
                  <div className={`border-2 rounded-xl p-4 inline-block ${
                    darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-lg shadow-md"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Stock Information Section */}
            <div className={`px-4 sm:px-6 py-4 sm:py-6 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  darkMode ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20' : 'bg-gradient-to-br from-orange-50 to-orange-100'
                }`}>
                  <FaBox className={`text-base sm:text-xl ${
                    darkMode ? 'text-orange-400' : 'text-orange-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`text-base sm:text-lg font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Initial Stock Information
                  </h3>
                  <p className={`text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Set opening stock levels for each location
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                {form.stock.map((stockItem, index) => {
                  const location = locations.find(loc => loc._id === stockItem.location);
                  return (
                    <div key={stockItem.location} className={`rounded-xl border-2 p-4 transition-all duration-300 ${
                      darkMode 
                        ? 'bg-gradient-to-br from-gray-800/40 to-gray-800/20 border-gray-700/50 hover:border-gray-600 hover:shadow-xl' 
                        : 'bg-gradient-to-br from-white to-gray-50/50 border-gray-200 hover:border-green-300 hover:shadow-lg'
                    }`}>
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          darkMode ? 'bg-green-500/20' : 'bg-green-100'
                        }`}>
                          <FaMapMarkerAlt className={`text-sm ${
                            darkMode ? 'text-green-400' : 'text-green-600'
                          }`} />
                        </div>
                        <span className={`text-sm font-bold tracking-wide uppercase ${
                          darkMode ? 'text-green-300' : 'text-green-900'
                        }`}>{location?.name || location?.locationName || 'Unknown Location'}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Fresh Quantity */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-green-400' : 'bg-green-500'
                            }`}></div>
                            Fresh Quantity
                          </label>
                          <input
                            type="number"
                            value={stockItem.freshQuantity}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].freshQuantity = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-green-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                        
                        {/* Damaged Quantity */}
                        <div className="space-y-1.5">
                          <label className={`flex items-center gap-1.5 text-xs font-semibold ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              darkMode ? 'bg-red-400' : 'bg-red-500'
                            }`}></div>
                            Damaged Quantity
                          </label>
                          <input
                            type="number"
                            value={stockItem.damagedQuantity}
                            onChange={(e) => {
                              const updatedStock = [...form.stock];
                              updatedStock[index].damagedQuantity = parseInt(e.target.value) || 0;
                              setForm(prev => ({ ...prev, stock: updatedStock }));
                            }}
                            className={`block w-full px-3 py-2.5 text-sm font-medium border-2 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-red-50/30'
                            }`}
                            min="0"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form Actions */}
            <div className={`px-4 sm:px-6 py-5 sm:py-6 flex flex-col sm:flex-row items-center justify-end gap-3 sm:gap-4 rounded-b-2xl transition-colors duration-300 ${
              darkMode ? 'bg-gradient-to-r from-gray-800/80 to-gray-800/60' : 'bg-gradient-to-r from-gray-50 to-gray-100/80'
            }`}>
              <button
                type="button"
                onClick={() => {
                  const initialStock = locations.map(location => ({
                    location: location._id,
                    freshQuantity: 0,
                    damagedQuantity: 0
                  }));
                  
                  setForm({
                    name: '',
                    brand: '',
                    category: '',
                    unitOfMeasure: '',
                    image: null,
                    stock: initialStock
                  });
                  setSelectedBrand(null);
                  setSelectedCategory(null);
                  setSelectedUnitOfMeasure(null);
                  setImagePreview(null);
                  setStockImage(null);
                }}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold border-2 rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-300 ${
                  darkMode 
                    ? 'border-gray-500 text-gray-300 bg-gray-700/50 hover:bg-gray-600 hover:border-gray-400 focus:ring-gray-500' 
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:ring-gray-400'
                }`}
              >
                Reset Form
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full sm:w-auto px-8 sm:px-10 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-green-500/25 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Creating Stock Item...</span>
                  </>
                ) : (
                  <>
                    <FaPlus className="text-sm sm:text-base" />
                    <span>Create Stock Item</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal />
    </div>
  );
}